import { Box, Collapse, ListItemButton, ListItemText, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

const PREFIX = 'CardHeader';

const classes = {
  box: `${PREFIX}-box`,
  default: `${PREFIX}-default`,
  label: `${PREFIX}-label`,
};

type StyleProps = {
  padding: number;
  marginBottom: number;
  marginTop: number;
};

const StyledCardHeader = styled('div', { label: 'CardHeader' })<StyleProps>(({ theme, marginBottom, marginTop }) => ({
  borderRadius: 8,
  marginBottom: theme.spacing(marginBottom),
  marginTop: theme.spacing(marginTop),
  background: theme.palette.common.white,
  [`&.${classes.default}`]: {
    background: theme.palette.common.white,
  },
  [`& .${classes.box}`]: {
    paddingLeft: '4px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  ['.MuiTableCell-body']: {
    backgroundColor: `${theme.palette.lightBlue.o25} !important`,
  },
  [`& .MuiCollapse-wrapper`]: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 0),
    },
  },
}));

const TitleBox = styled(Box)<StyleProps>(({ theme }) => ({
  padding: theme.spacing(2, 4, 2),
  borderBottom: `1px solid ${theme.palette.bluViu.o50}50`,
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 500,
  color: 'black',
  textTransform: 'none',
  padding: '0 10px 0 0',
}));

type Props = {
  children: JSX.Element;
  padding?: number;
  marginBottom?: number;
  marginTop?: number;
  elevation?: number;
  id?: string;
  sx?: SxProps<Theme> | undefined;
  fontWeight?: number;
  hiddenLabel?: boolean;
  hideBorder?: boolean;
  label?: string;
};

const CardHeader: React.FC<Props> = ({
  children,
  label,
  padding = 4,
  sx,
  hideBorder = false,
  marginBottom = 2,
  marginTop = 0,
  fontWeight = 700,
  hiddenLabel,
  ...props
}) => {
  const theme = useTheme();

  return (
    <StyledCardHeader
      sx={{ ...sx, border: hideBorder ? 'none' : `1px solid ${theme.palette.darkBlue.main}` }}
      className={classes.default}
      padding={padding}
      marginBottom={marginBottom}
      marginTop={marginTop}>
      <TitleBox padding={padding} marginBottom={0} marginTop={0} fontWeight={fontWeight} className={classes.default}>
        <ListItemButton
          disableRipple
          className={classes.box}
          sx={{
            padding: `${theme.spacing(1, 0, 1)}`,
            cursor: 'default',
          }}>
          <ListItemText primary={<CardLabel>{label}</CardLabel>} />
        </ListItemButton>
      </TitleBox>

      <Collapse {...props} in={true}>
        {children}
      </Collapse>
    </StyledCardHeader>
  );
};

export default CardHeader;
